import React, { useMemo, useState } from 'react'
import { useTranslation } from 'language/translator'

import {
  InnerWidgetPatchBody,
  TWidgetSettingsLoaded,
  WidgetType
} from 'types/GlobalWidget'
import { ComparativeDisplayType, RollingType } from 'types/GlobalKpiOption'
import {
  IMetric,
  ShowPerEnum,
  TCustomEconomyMetricVariant,
  isCustomEconomyMetric
} from 'types/GlobalMetric'
import { VariantOption } from 'types/GlobalVariantOption'

import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'

import DndList from 'components_new/molecules/DndList'
import { Option } from 'components_new/molecules/SelectSearch'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import DragItem from '../../DatasetsSection/dialogs/EditOrderDialog/DragItem'
import { getIconName } from '../../DatasetsSection/utils'
import { FINANCIAL_YEAR_PERIOD_FILTERS } from 'utils/constants'

import ComparativeDisplayTypeSection from 'components_new/molecules/ComparativeDisplayTypeSection'
import DistributionSection from 'components_new/molecules/DistributionSection'
import RollingSection from 'components_new/molecules/RollingSection'
import DecimalsSection from 'components_new/molecules/DecimalsSection'

import { useMetrics } from 'redux/hooks/Metrics'

import { VARIANT_SUBCATEGORY_ORDER } from 'utils/metric'

/**
 * The variant catalogue of the widget's metrics: the same standard
 * economy metric set for every custom metric, so the first one
 * carries the options. Empty when the widget does not support
 * comparison figures.
 */
export const useVariantCatalogue = (
  widget: TWidgetSettingsLoaded
): {
  variants: TCustomEconomyMetricVariant[]
  defaultVariantId: string | null
} => {
  const [metrics] = useMetrics()

  const metricById = useMemo(() => {
    const map: { [id: string]: IMetric } = {}

    metrics.forEach((metric) => {
      map[metric.id] = metric
    })

    return map
  }, [metrics])

  return useMemo(() => {
    if (widget.settings.type.selected !== WidgetType.LIST) {
      return { variants: [], defaultVariantId: null }
    }

    const allCustom =
      widget.settings.kpi_options.length > 0 &&
      widget.settings.kpi_options.every((option) =>
        isCustomEconomyMetric(metricById[option.kpi_template_id])
      )

    if (!allCustom) {
      return { variants: [], defaultVariantId: null }
    }

    const [firstOption] = widget.settings.kpi_options
    const metric = firstOption ? metricById[firstOption.kpi_template_id] : null

    if (!isCustomEconomyMetric(metric)) {
      return { variants: [], defaultVariantId: null }
    }

    return {
      variants: metric.variants,
      defaultVariantId: metric.default_variant ?? null
    }
  }, [widget.settings.type.selected, widget.settings.kpi_options, metricById])
}

/**
 * The variants as select options, grouped by their subcategory in the
 * outcome/budget/forecast order rather than alphabetically.
 */
export const getVariantSelectOptions = (
  variants: TCustomEconomyMetricVariant[],
  lang: 'sv' | 'en'
): Option[] =>
  [...variants]
    .sort((a, b) => {
      const indexA = VARIANT_SUBCATEGORY_ORDER.indexOf(a.subcategory.slug)
      const indexB = VARIANT_SUBCATEGORY_ORDER.indexOf(b.subcategory.slug)

      if (indexA === indexB) {
        return 0
      }

      if (indexA === -1) {
        return 1
      }

      if (indexB === -1) {
        return -1
      }

      return indexA - indexB
    })
    .map((variant) => ({
      id: variant.id,
      title: variant.title[lang],
      group: variant.subcategory.title[lang]
    }))

interface VariantOptionSectionProps {
  createVariantOptionBy: (variantKpiTemplateId: string, title: string) => void
  deleteVariantOptionBy: (variantOptionId: string) => void
  onClose: () => void
  updateVariantOptionBy: (
    variantOptionId: string,
    body: {
      active_show_per?: ShowPerEnum | null
      title?: string
      number_of_decimals?: number
      rolling_active?: RollingType | null
      comparative_display_type?: ComparativeDisplayType
    }
  ) => void
  updateWidget: (body: InnerWidgetPatchBody) => void
  widget: TWidgetSettingsLoaded
}

const VariantOptionSection = (props: VariantOptionSectionProps) => {
  const {
    createVariantOptionBy,
    deleteVariantOptionBy,
    onClose,
    updateVariantOptionBy,
    updateWidget,
    widget
  } = props

  const { t, lang } = useTranslation()
  const { variants } = useVariantCatalogue(widget)

  const [drawer, setDrawer] = useState<{
    open: boolean
    variantOptionId: string | null
  }>({
    open: false,
    variantOptionId: null
  })
  const [dialog, setDialog] = useState<{
    open: boolean
    property: 'variant' | 'title'
    variant: 'add' | 'edit'
    value: string
  }>({
    open: false,
    property: 'variant',
    variant: 'add',
    value: ''
  })
  const [orderDialog, setOrderDialog] = useState<{
    open: boolean
    ids: string[]
  }>({
    open: false,
    ids: []
  })

  if (variants.length === 0) {
    return null
  }

  const columns = widget.settings.variant_options
  const drawerColumn = drawer.variantOptionId
    ? columns.find((column) => column.id === drawer.variantOptionId) ?? null
    : null

  const getVariant = (
    column: VariantOption
  ): TCustomEconomyMetricVariant | null =>
    variants.find((variant) => variant.id === column.variant_kpi_template_id) ??
    null

  /*-- drawer --*/
  const handleBackDrawer = () => {
    setDrawer({ open: false, variantOptionId: null })
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  /*-- dialogs --*/
  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (
    property: 'variant' | 'title',
    variant: 'add' | 'edit'
  ) => {
    let value = ''

    if (variant === 'edit' && drawerColumn) {
      value = drawerColumn.title
    }

    setDialog({ open: true, property, variant, value })
  }

  const handleSubmitDialog = () => {
    if (dialog.variant === 'add') {
      const variant = variants.find((item) => item.id === dialog.value)

      if (variant) {
        createVariantOptionBy(variant.id, variant.title[lang])
      }

      return
    }

    if (!drawer.variantOptionId) return

    // an emptied name resets to the variant's own title
    updateVariantOptionBy(drawer.variantOptionId, {
      title: dialog.value.trim() || drawerVariant?.title[lang] || ''
    })
  }

  const handleToggleDistribution = (next: ShowPerEnum | null) => {
    if (!drawerColumn) return

    updateVariantOptionBy(drawerColumn.id, { active_show_per: next })
  }

  const handleDelete = () => {
    if (drawer.variantOptionId) {
      deleteVariantOptionBy(drawer.variantOptionId)
      handleBackDrawer()
    }
  }

  /*-- order --*/
  const handleOpenOrderDialog = () => {
    setOrderDialog({ open: true, ids: columns.map((column) => column.id) })
  }

  const handleCloseOrderDialog = () => {
    setOrderDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleSubmitOrderDialog = () => {
    updateWidget({ variant_option_order: orderDialog.ids })
    handleCloseOrderDialog()
  }

  // a financial year period rules out rolling sums and some
  // distributions
  const isFinancialYear = FINANCIAL_YEAR_PERIOD_FILTERS.includes(
    widget.settings.date_filter.selected.value
  )

  const variantSelectOptions = getVariantSelectOptions(variants, lang)

  const drawerVariant = drawerColumn ? getVariant(drawerColumn) : null

  return (
    <>
      <SettingsGroup
        actions={
          columns.length > 1 ? (
            <Link onClick={handleOpenOrderDialog} variant="body2">
              <Icon
                color="inherit"
                fontSize="inherit"
                name={'Edit'}
                sx={{
                  mr: '0.25em'
                }}
              />
              {t('global', 'edit_order')}
            </Link>
          ) : undefined
        }
        helperText={t('widgets', 'variant_options_helper')}
        title={t('widgets', 'variant_options_title')}
      >
        {columns.map((column) => (
          <SettingsItem
            key={column.id}
            onClick={() =>
              setDrawer({ open: true, variantOptionId: column.id })
            }
            title={column.title}
          />
        ))}
        <SettingsItem
          disabled={widget.settings.is_preview}
          color="success"
          onClick={() => handleOpenDialog('variant', 'add')}
          title={t('global', 'add')}
          variant="add"
        />
      </SettingsGroup>

      {/*-- drawer --*/}
      <SettingsDrawer
        open={drawer.open}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={t('widgets', 'variant_options_title')}
      >
        {drawerColumn ? (
          <>
            <SettingsGroup>
              <SettingsItem
                onClick={() => handleOpenDialog('title', 'edit')}
                title={t('widgets', 'display_name')}
                value={drawerColumn.title}
                variant="edit"
              />
              <SettingsItem
                title={t('widgets', 'variant_option_variant_title')}
                value={drawerVariant?.title[lang] ?? ''}
              />
            </SettingsGroup>

            <DecimalsSection
              numberOfDecimals={drawerColumn.number_of_decimals ?? 0}
              onChange={(number_of_decimals) =>
                updateVariantOptionBy(drawerColumn.id, { number_of_decimals })
              }
            />

            <RollingSection
              isFinancialYear={isFinancialYear}
              onChange={(rolling_active) =>
                updateVariantOptionBy(drawerColumn.id, { rolling_active })
              }
              options={drawerVariant?.rolling_sums ?? []}
              selected={drawerColumn.rolling_active}
            />

            <DistributionSection
              distributions={drawerVariant?.distributions ?? []}
              isFinancialYear={isFinancialYear}
              onToggle={handleToggleDistribution}
              selected={drawerColumn.active_show_per}
              subject={drawerColumn.title}
            />

            <ComparativeDisplayTypeSection
              hasComparativePeriod={
                !!widget.settings.comparative_period.selected?.value
              }
              onChange={(comparative_display_type) =>
                updateVariantOptionBy(drawerColumn.id, {
                  comparative_display_type
                })
              }
              options={drawerColumn.comparative_display_type.options}
              selected={drawerColumn.comparative_display_type.selected}
            />

            <SettingsGroup>
              <SettingsItem
                color="error"
                disabled={columns.length === 1}
                disabledText={t('widgets', 'cannot_delete_last_variant_option')}
                onClick={handleDelete}
                title={t('global', 'delete')}
                variant="action"
              />
            </SettingsGroup>
          </>
        ) : null}
      </SettingsDrawer>

      {/*-- dialogs --*/}
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleCloseOrderDialog} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSubmitOrderDialog}>
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={handleCloseOrderDialog}
        open={orderDialog.open}
        sx={{
          zIndex: 1302 // above the settings drawer, like SettingsEditDialog
        }}
        title={t('global', 'edit_order')}
      >
        <DndList
          editable={true}
          itemGap={8}
          itemHeight={40}
          handleDragEnd={(newOrder) =>
            setOrderDialog((prevState) => ({ ...prevState, ids: newOrder }))
          }
          items={orderDialog.ids.map((id, index) => {
            const column = columns.find((item) => item.id === id)

            return {
              id,
              content: (
                <DragItem
                  hidden={false}
                  iconName={getIconName(index + 1, widget.settings)}
                  index={index + 1}
                  title={column ? column.title : ''}
                />
              ),
              containerSx: {
                p: 1
              }
            }
          })}
        />
      </SimpleDialog>
      <SettingsEditDialog
        onChange={(value: string) =>
          setDialog((prevState) => ({ ...prevState, value }))
        }
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={variantSelectOptions}
        title={
          dialog.variant === 'add'
            ? t('widgets', 'variant_options_title')
            : dialog.property === 'variant'
              ? t('widgets', 'variant_option_variant_title')
              : t('widgets', 'display_name')
        }
        type={dialog.property === 'title' ? 'text' : 'select'}
        value={dialog.value}
        variant={dialog.variant}
      />
    </>
  )
}

export default VariantOptionSection
